import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Github, LinkedinIcon } from "lucide-react";
import { SiLeetcode } from "react-icons/si";
import { useSelector } from "react-redux";
import SkillsSection from "./SkillsSection";
import Projects from "./Projects";
import AboutMeSection from "./AboutMeSection";
import Education from "./Education";

const MainSection = () => {
  const theme = useSelector((state) => state.theme.theme);
  const [drops, setDrops] = useState([]);
  const [splashes, setSplashes] = useState([]);

  useEffect(() => {
    setDrops(
      Array.from({ length: 50 }).map((_, i) => ({
        id: i,
        left: `${Math.random() * 100}vw`,
        animationDuration: `${Math.random() * 2 + 1}s`,
        animationDelay: `${Math.random() * 2}s`,
      }))
    );
  }, []);

  const handleAnimationEnd = (drop) => {
    setSplashes((prev) => [
      ...prev,
      { id: drop.id, left: drop.left, timestamp: Date.now() },
    ]);
    setTimeout(
      () => setSplashes((prev) => prev.filter((s) => s.id !== drop.id)),
      500
    );
  };

  return (
    <>
      <div className="relative flex flex-col lg:flex-row justify-center lg:justify-between items-center overflow-hidden">
        {/* Background Drops */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          {drops.map((drop) => (
            <motion.div
              key={drop.id}
              className="absolute w-[3px] h-[20px] bg-gradient-to-b from-pink-500 to-blue-500 opacity-70"
              style={{ left: drop.left, top: "-50px" }}
              animate={{ y: ["0%", "100vh"], opacity: [0.7, 0] }}
              transition={{
                duration: parseFloat(drop.animationDuration),
                delay: parseFloat(drop.animationDelay),
                ease: "linear",
                repeat: Infinity,
              }}
              onAnimationComplete={() => handleAnimationEnd(drop)}
            />
          ))}
          {splashes.map((splash) => (
            <motion.div
              key={splash.timestamp}
              className="absolute w-[8px] h-[4px] rounded-full bg-blue-500 opacity-70"
              style={{ left: splash.left, bottom: "10px" }}
              initial={{ scale: 0, opacity: 0.7 }}
              animate={{ scale: 1.5, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          ))}
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 relative z-10 flex flex-col lg:flex-row justify-center lg:justify-between items-center mb-4 md:mb-4">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="space-y-6 lg:w-1/2 text-center lg:text-left flex flex-col items-center lg:items-start order-last lg:order-first"
          >
            <h1 className="text-[28px] lg:text-[48px] font-semibold">
              Hello, I'm
              <span className="font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[#007AFF] via-[#5E5CE6] to-[#B84FFF] ml-2">
                Naveen Kumar
              </span>
            </h1>
            <h2 className="text-[24px] lg:text-[40px] font-medium mt-2">
              <span className="font-bold text-gray-800 dark:text-white">
                MERN STACK
              </span>{" "}
              <span className="font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#007AFF] via-[#5E5CE6] to-[#B84FFF]">
                Developer
              </span>
            </h2>

            <motion.p
              className="tracking-tight text-gray-500 text-sm sm:text-base"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2 }}
            >
              Naveen Kumar is a Computer Science student at Garden City
              University, passionate about mernstack development and modern web
              technologies. I specialize in creating responsive web applications
              using React.js and Tailwind CSS, NodeJS, Express, MongoDB with a
              focus on UI/UX design, smooth animations, and performance
              optimization.
            </motion.p>

            <div className="flex space-x-7">
              <motion.a
                whileHover={{ scale: 1.2, rotate: 10 }}
                whileTap={{ scale: 0.9 }}
                href="https://github.com/thephotogenicbug"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 py-3 px-4 rounded-md bg-gradient-to-r from-[#007AFF] via-[#5E5CE6] to-[#B84FFF] text-white shadow-lg"
              >
                <Github />
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.2, rotate: -10 }}
                whileTap={{ scale: 0.9 }}
                href="https://www.linkedin.com/in/thephotogenicbug/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 py-3 px-4 rounded-md bg-gradient-to-r from-[#007AFF] via-[#5E5CE6] to-[#B84FFF] text-white shadow-lg"
              >
                <LinkedinIcon />
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.2, rotate: -10 }}
                whileTap={{ scale: 0.9 }}
                href="https://leetcode.com/u/naveenkumar__05/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 py-3 px-4 rounded-md bg-gradient-to-r from-[#007AFF] via-[#5E5CE6] to-[#B84FFF] text-white shadow-lg"
              >
                <SiLeetcode size={24} />
              </motion.a>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:w-1/2 flex justify-center items-center order-first lg:order-last"
          >
            <motion.img
              src="https://media.licdn.com/dms/image/v2/D5603AQFQE0FsHq-3cg/profile-displayphoto-shrink_400_400/B56Zdq1jEDGQAo-/0/1749844112786?e=1755734400&v=beta&t=x-2-2CQjGnnQoKJz5T_3u7dH7OgA-XGqDdTYhnmngIA"
              alt="Naveen Kumar"
              className="w-full max-w-[300px] lg:max-w-[350px] rounded-full shadow-lg"
              whileHover={{ scale: 1.05 }}
              animate={{ y: [0, -5, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
            />
          </motion.div>
        </div>
      </div>

      {/* Additional Sections */}
      <SkillsSection />
      <Education />
      <Projects />
      {/* <AboutMeSection /> */}
    </>
  );
};

export default MainSection;
